/**
 * Motore condiviso di sessione per inattività (scope `tenant` | `master`).
 *
 * CONTRATTO per gli adapter (frontend Easyflow, master, W3 Suite):
 *
 * 1. Fonte di verità = UNA deadline wall-clock `lastActivity + idleTimeoutMs`
 *    persistita in localStorage (chiave namespaced per scope) e condivisa da
 *    tutte le tab dello stesso origin. Ogni tab tiene una copia in memoria e
 *    si riallinea via evento `storage`: attività in QUALUNQUE tab rinnova la
 *    sessione per tutte.
 *
 * 2. Attività = SOLO eventi utente reali (`mousedown`, `keydown`, `touchstart`,
 *    `click`, `input` subito; `mousemove`/`scroll`/`wheel` con throttle).
 *    NON contano come attività: il rinnovo automatico del JWT, il ritorno in
 *    primo piano (`visibilitychange`/`pageshow`/`focus` sono solo un
 *    ricontrollo della deadline), le chiamate API in background.
 *
 * 3. Scadenza: alla deadline `onExpire` è chiamata UNA volta per tab. La tab
 *    che la rileva per prima scrive un marker (deadline + id tab) in
 *    localStorage e riceve `isInitiator: true` (è lei a revocare la sessione
 *    lato server); le altre si allineano via `storage` e ricevono
 *    `isInitiator: false` (solo pulizia locale + redirect al login). Due store
 *    dello stesso scope nella stessa pagina non chiamano `onExpire` due volte.
 *    L'elezione è BEST-EFFORT: localStorage non ha compare-and-set, quindi due
 *    tab che scadono nello stesso istante possono entrambe risultare
 *    initiator. La revoca lato server dell'adapter DEVE essere idempotente
 *    (un secondo logout sulla stessa sessione = no-op, mai errore bloccante).
 *
 * 4. Rinnovo token: prima della deadline, quando `exp` dell'access token
 *    (letto da `getAccessToken`) è sotto `refreshThresholdMs`, viene chiamata
 *    `onRefreshNeeded` in single-flight. Mai dopo la scadenza per inattività.
 *
 * 5. Durate: arrivano da `start(config)`/`updateConfig(partial)` (es. policy
 *    del server convertita con `sessionPolicyToConfig`). `DEFAULT_SESSION_POLICY`
 *    è l'unico fallback. `updateConfig` ricalcola la deadline dall'ultima
 *    attività reale senza azzerarla.
 *
 * 6. Avvio: `start(config, { reason })` distingue `login` (nuova sessione:
 *    il login è attività, deadline/marker precedenti scartati) da `restore`
 *    (default al bootstrap pagina: adotta la deadline persistita — se è nel
 *    passato il tick immediato fa scadere — e rispetta il marker di scadenza
 *    lasciato da un'altra tab → `onExpire` come follower).
 *
 * 6b. Una deadline superata non è MAI rinnovabile: `touch()` e
 *    `updateConfig()` controllano la scadenza prima di spostarla (tab
 *    sospesa che riprende con un click → `onExpire`, non rinnovo).
 *
 * 7. `stop()` rimuove listener/timer e la deadline persistita (logout
 *    esplicito). Le chiavi legacy dei vecchi tracker per-frontend vengono
 *    rimosse una tantum allo `start`.
 */
import { browserLifecycle } from '../connection-lifecycle';
import { decodeJwtExpMs } from '../jwt';
import { DEFAULT_SESSION_POLICY, SESSION_ENGINE_TIMING } from './policy';
import type {
  SessionDurations,
  SessionEngineTiming,
  SessionIdleConfig,
  SessionIdleListener,
  SessionIdleSnapshot,
  SessionIdleStatus,
  SessionIdleStore,
  SessionScope,
  SessionStartOptions,
} from './types';

const STORAGE_PREFIX = 'easyflow:session-idle:';

export function sessionDeadlineStorageKey(scope: SessionScope): string {
  return `${STORAGE_PREFIX}${scope}:deadline`;
}

export function sessionExpiredStorageKey(scope: SessionScope): string {
  return `${STORAGE_PREFIX}${scope}:expired`;
}

/**
 * Chiavi dei tracker legacy per-frontend, rimosse una tantum allo `start`
 * (il motore condiviso le sostituisce; i consumer vengono eliminati nei task
 * di adozione di ciascun frontend).
 */
export const LEGACY_SESSION_STORAGE_KEYS: Readonly<Record<SessionScope, readonly string[]>> = Object.freeze({
  tenant: Object.freeze(['edgvoip_last_activity']),
  master: Object.freeze(['edgvoip_master_session_deadline']),
});

const INSTANT_ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'click', 'input'] as const;
const THROTTLED_ACTIVITY_EVENTS = ['mousemove', 'scroll', 'wheel'] as const;
const ACTIVITY_LISTENER_OPTIONS: AddEventListenerOptions = { passive: true, capture: true };

function hasDom(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage non disponibile (quota/privacy): si lavora solo in memoria.
  }
}

function removeStorage(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // idem
  }
}

/** Id stabile di questa tab/realm per l'elezione dell'initiator. */
const REALM_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

interface ExpiredMarker {
  readonly deadline: number;
  readonly realmId: string;
}

/** Marker di scadenza: `<deadline>:<realmId>`. */
function parseExpiredMarker(raw: string | null): ExpiredMarker | null {
  if (raw === null) return null;
  const sep = raw.indexOf(':');
  const deadline = parseTimestamp(sep === -1 ? raw : raw.slice(0, sep));
  if (deadline === null) return null;
  return { deadline, realmId: sep === -1 ? '' : raw.slice(sep + 1) };
}

function parseTimestamp(raw: string | null): number | null {
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return typeof value === 'object' && value !== null && 'then' in value && typeof value.then === 'function';
}

function resolveDurations(base: SessionDurations, partial: Partial<SessionDurations>): SessionDurations {
  return {
    idleTimeoutMs: partial.idleTimeoutMs ?? base.idleTimeoutMs,
    warnBeforeMs: partial.warnBeforeMs ?? base.warnBeforeMs,
    refreshThresholdMs: partial.refreshThresholdMs ?? base.refreshThresholdMs,
  };
}

function resolveTiming(base: SessionEngineTiming, partial: Partial<SessionEngineTiming>): SessionEngineTiming {
  return {
    tickIntervalMs: partial.tickIntervalMs ?? base.tickIntervalMs,
    activityThrottleMs: partial.activityThrottleMs ?? base.activityThrottleMs,
  };
}

/**
 * Deadline già gestite in QUESTA pagina (realm JS) per scope: evita il doppio
 * `onExpire` quando due istanze dello stesso scope convivono nello stesso
 * documento (gli eventi `storage` non arrivano al documento che scrive).
 */
const expiredInRealm = new Map<SessionScope, number>();

class SessionIdleStoreImpl implements SessionIdleStore {
  public readonly scope: SessionScope;

  private config: SessionIdleConfig | null = null;
  private durations: SessionDurations = DEFAULT_SESSION_POLICY;
  private timing: SessionEngineTiming = SESSION_ENGINE_TIMING;
  private status: SessionIdleStatus = 'stopped';
  private deadline: number | null = null;
  private lastActivity: number | null = null;
  private lastThrottledTouch = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lifecycleUnsubscribe: (() => void) | null = null;
  private refreshInFlight: PromiseLike<unknown> | null = null;
  private readonly listeners = new Set<SessionIdleListener>();

  constructor(scope: SessionScope) {
    this.scope = scope;
  }

  // ------------------------------------------------------------------ API

  public start(config: SessionIdleConfig, options?: SessionStartOptions): void {
    this.applyConfig(config);
    if (!hasDom()) return;
    const reason = options?.reason ?? 'restore';

    if (this.status === 'running') {
      if (reason === 'login') {
        // Nuova sessione su store già attivo: il login è attività.
        this.clearExpiryMarkers();
        this.setDeadlineFromActivity(Date.now());
      } else if (!this.expireIfDeadlinePassed()) {
        // Idempotente: durate/callback aggiornate, deadline ricalcolata.
        this.recomputeDeadlineFromLastActivity();
      }
      this.emit();
      return;
    }

    for (const legacyKey of LEGACY_SESSION_STORAGE_KEYS[this.scope]) {
      removeStorage(legacyKey);
    }

    this.status = 'running';
    this.refreshInFlight = null;
    this.lastThrottledTouch = 0;

    if (reason === 'login') {
      this.clearExpiryMarkers();
      this.setDeadlineFromActivity(Date.now());
    } else {
      const persisted = parseTimestamp(readStorage(sessionDeadlineStorageKey(this.scope)));
      const expiredMarker = parseExpiredMarker(readStorage(sessionExpiredStorageKey(this.scope)));
      if (persisted !== null) {
        // Sessione viva (o da far scadere al tick immediato): la dedup
        // in-pagina riguarda solo scadenze già consumate, si riparte pulita.
        expiredInRealm.delete(this.scope);
        this.deadline = persisted;
        this.lastActivity = persisted - this.durations.idleTimeoutMs;
      } else if (expiredMarker !== null) {
        // Un'altra tab ha già fatto scadere la sessione: niente nuovo orologio.
        this.expire(expiredMarker.deadline, false);
        return;
      } else {
        expiredInRealm.delete(this.scope);
        this.setDeadlineFromActivity(Date.now());
      }
    }

    this.installListeners();
    this.intervalId = setInterval(this.handleInterval, this.timing.tickIntervalMs);
    this.tick();
  }

  public stop(): void {
    const wasActive = this.status !== 'stopped';
    this.teardown();
    this.status = 'stopped';
    this.deadline = null;
    this.lastActivity = null;
    if (hasDom()) removeStorage(sessionDeadlineStorageKey(this.scope));
    if (wasActive) this.emit();
  }

  public touch(): void {
    if (this.status !== 'running') return;
    // Attività dopo una deadline superata (tab sospesa, tick non ancora
    // scattato) NON rinnova: la sessione scade.
    if (this.expireIfDeadlinePassed()) return;
    this.setDeadlineFromActivity(Date.now());
    this.emit();
  }

  public updateConfig(partial: Partial<Omit<SessionIdleConfig, 'scope'>>): void {
    const base: SessionIdleConfig = this.config ?? { scope: this.scope, onExpire: () => undefined };
    this.applyConfig({ ...base, ...partial, scope: this.scope });
    if (this.status !== 'running') return;
    if (this.expireIfDeadlinePassed()) return;
    if (this.intervalId !== null && partial.tickIntervalMs !== undefined) {
      clearInterval(this.intervalId);
      this.intervalId = setInterval(this.handleInterval, this.timing.tickIntervalMs);
    }
    this.recomputeDeadlineFromLastActivity();
    this.emit();
  }

  public getDeadline(): number | null {
    return this.status === 'running' ? this.deadline : null;
  }

  public getRemainingMs(): number {
    if (this.status !== 'running' || this.deadline === null) return 0;
    return Math.max(0, this.deadline - Date.now());
  }

  public getStatus(): SessionIdleStatus {
    return this.status;
  }

  public getSnapshot(): SessionIdleSnapshot {
    return {
      scope: this.scope,
      status: this.status,
      deadline: this.getDeadline(),
      remainingMs: this.getRemainingMs(),
      durations: this.durations,
    };
  }

  public subscribe(listener: SessionIdleListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // ------------------------------------------------------------ internals

  private applyConfig(config: SessionIdleConfig): void {
    this.config = config;
    this.durations = resolveDurations(DEFAULT_SESSION_POLICY, config);
    this.timing = resolveTiming(SESSION_ENGINE_TIMING, config);
  }

  private clearExpiryMarkers(): void {
    removeStorage(sessionExpiredStorageKey(this.scope));
    expiredInRealm.delete(this.scope);
  }

  /**
   * Ricontrollo wall-clock della sola scadenza (adotta prima un'eventuale
   * deadline più avanti scritta da un'altra tab). Ritorna true se ha fatto
   * scadere la sessione.
   */
  private expireIfDeadlinePassed(): boolean {
    if (this.status !== 'running' || this.deadline === null) return false;
    const persisted = parseTimestamp(readStorage(sessionDeadlineStorageKey(this.scope)));
    if (persisted !== null && persisted > this.deadline) {
      this.deadline = persisted;
      this.lastActivity = persisted - this.durations.idleTimeoutMs;
    }
    if (Date.now() < this.deadline) return false;
    this.claimAndExpire(this.deadline);
    return true;
  }

  private setDeadlineFromActivity(activityAt: number): void {
    this.lastActivity = activityAt;
    this.deadline = activityAt + this.durations.idleTimeoutMs;
    writeStorage(sessionDeadlineStorageKey(this.scope), String(this.deadline));
  }

  private recomputeDeadlineFromLastActivity(): void {
    if (this.lastActivity === null) return;
    const next = this.lastActivity + this.durations.idleTimeoutMs;
    if (next === this.deadline) return;
    this.deadline = next;
    writeStorage(sessionDeadlineStorageKey(this.scope), String(next));
  }

  private installListeners(): void {
    for (const type of INSTANT_ACTIVITY_EVENTS) {
      window.addEventListener(type, this.handleInstantActivity, ACTIVITY_LISTENER_OPTIONS);
    }
    for (const type of THROTTLED_ACTIVITY_EVENTS) {
      window.addEventListener(type, this.handleThrottledActivity, ACTIVITY_LISTENER_OPTIONS);
    }
    window.addEventListener('storage', this.handleStorage);
    this.lifecycleUnsubscribe = browserLifecycle.subscribe((event) => {
      if (event.type === 'foreground') this.tick();
    });
  }

  private teardown(): void {
    if (!hasDom()) return;
    for (const type of INSTANT_ACTIVITY_EVENTS) {
      window.removeEventListener(type, this.handleInstantActivity, ACTIVITY_LISTENER_OPTIONS);
    }
    for (const type of THROTTLED_ACTIVITY_EVENTS) {
      window.removeEventListener(type, this.handleThrottledActivity, ACTIVITY_LISTENER_OPTIONS);
    }
    window.removeEventListener('storage', this.handleStorage);
    if (this.lifecycleUnsubscribe) {
      this.lifecycleUnsubscribe();
      this.lifecycleUnsubscribe = null;
    }
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.refreshInFlight = null;
  }

  private readonly handleInstantActivity = (): void => {
    this.touch();
  };

  private readonly handleThrottledActivity = (): void => {
    const now = Date.now();
    if (now - this.lastThrottledTouch < this.timing.activityThrottleMs) return;
    this.lastThrottledTouch = now;
    this.touch();
  };

  private readonly handleInterval = (): void => {
    this.tick();
  };

  private readonly handleStorage = (event: StorageEvent): void => {
    if (this.status !== 'running') return;
    if (event.key === sessionDeadlineStorageKey(this.scope)) {
      const next = parseTimestamp(event.newValue);
      // `null` = altra tab ha fatto stop/expire: la scadenza arriva dal marker
      // dedicato, il logout esplicito lo propaga l'adapter.
      if (next === null || next === this.deadline) return;
      this.deadline = next;
      this.lastActivity = next - this.durations.idleTimeoutMs;
      this.emit();
      return;
    }
    if (event.key === sessionExpiredStorageKey(this.scope)) {
      const marker = parseExpiredMarker(event.newValue);
      if (marker === null) return;
      this.expire(marker.deadline, false);
    }
  };

  /** Ricontrollo wall-clock: scadenza prima, poi rinnovo token. */
  private tick(): void {
    if (this.status !== 'running' || this.deadline === null) return;
    if (this.expireIfDeadlinePassed()) return;
    this.maybeRequestRefresh(Date.now());
    this.emit();
  }

  /**
   * Elezione best-effort dell'initiator: se nessun'altra tab ha già marcato
   * questa deadline, scrive il marker col proprio id e lo rilegge; vince solo
   * chi ritrova il proprio id (un'altra tab può aver scritto nel frattempo).
   */
  private claimAndExpire(deadline: number): void {
    const expiredKey = sessionExpiredStorageKey(this.scope);
    const existing = parseExpiredMarker(readStorage(expiredKey));
    let isInitiator = false;
    if (existing === null || existing.deadline !== deadline) {
      writeStorage(expiredKey, `${deadline}:${REALM_ID}`);
      const confirmed = parseExpiredMarker(readStorage(expiredKey));
      isInitiator = confirmed === null || (confirmed.deadline === deadline && confirmed.realmId === REALM_ID);
      if (isInitiator) removeStorage(sessionDeadlineStorageKey(this.scope));
    }
    this.expire(deadline, isInitiator);
  }

  private expire(deadline: number, isInitiator: boolean): void {
    if (this.status !== 'running') return;
    const alreadyHandledHere = expiredInRealm.get(this.scope) === deadline;
    expiredInRealm.set(this.scope, deadline);
    const config = this.config;
    this.teardown();
    this.status = 'expired';
    this.emit();
    if (alreadyHandledHere || !config) return;
    config.onExpire({ scope: this.scope, reason: 'idle', deadline, isInitiator });
  }

  private maybeRequestRefresh(now: number): void {
    const config = this.config;
    if (!config?.getAccessToken || !config.onRefreshNeeded) return;
    if (this.refreshInFlight) return;
    const token = config.getAccessToken();
    if (!token) return;
    const expMs = decodeJwtExpMs(token);
    if (expMs === null) return;
    if (expMs - now > this.durations.refreshThresholdMs) return;

    const result = config.onRefreshNeeded();
    if (!isPromiseLike(result)) return;
    const inFlight = result.then(
      () => {
        if (this.refreshInFlight === inFlight) this.refreshInFlight = null;
      },
      () => {
        // L'esito del rinnovo lo gestisce l'adapter (es. logout su 401):
        // qui si garantisce solo il single-flight e che lo scheduler sopravviva.
        if (this.refreshInFlight === inFlight) this.refreshInFlight = null;
      },
    );
    this.refreshInFlight = inFlight;
  }

  private emit(): void {
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch {
        // Un subscriber che lancia non deve bloccare gli altri.
      }
    });
  }
}

/** Istanza indipendente (test/avanzato). In app usare `getSessionIdleStore`. */
export function createSessionIdleStore(scope: SessionScope): SessionIdleStore {
  return new SessionIdleStoreImpl(scope);
}

const registry = new Map<SessionScope, SessionIdleStore>();

/** Singleton per scope: adapter, hook e banner condividono la stessa istanza. */
export function getSessionIdleStore(scope: SessionScope): SessionIdleStore {
  let store = registry.get(scope);
  if (!store) {
    store = new SessionIdleStoreImpl(scope);
    registry.set(scope, store);
  }
  return store;
}
